/**
 * 404 page — intentionally marked noindex, nofollow so Googlebot never
 * accidentally indexes a soft-404. We can't force a real HTTP 404 from a
 * static SPA host, but the `noindex` meta + explicit copy makes the state
 * clear to crawlers and users.
 */
import { Link } from "react-router-dom";
import { AlertTriangle, Home, Search } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import usePageSeo from "@/hooks/usePageSeo";

export default function NotFound() {
  usePageSeo({
    title: "Page not found — WebsiteTech.pro",
    description: "The page you're looking for doesn't exist. Browse our services, lead generation offerings, or run a free website audit.",
    noindex: true,
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="pt-28 pb-20" data-testid="not-found-page">
        <div className="max-w-xl mx-auto px-4 py-20 text-center">
          <AlertTriangle size={44} className="mx-auto text-amber-500" />
          <p className="mt-4 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">Error 404</p>
          <h1 className="mt-2 font-heading font-extrabold text-slate-900 text-4xl sm:text-5xl">Page not found.</h1>
          <p className="mt-3 text-slate-600">
            The link you followed might be broken or the page has moved. Try one of these instead:
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/" className="btn-primary rounded-lg px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
              <Home size={14} /> Back home
            </Link>
            <Link to="/lead-generation" className="rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:border-brand hover:text-brand">
              Explore services
            </Link>
            <Link to="/seo-analyzer" className="rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:border-brand hover:text-brand inline-flex items-center gap-2">
              <Search size={14} /> Run free audit
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
